import { $t } from '@/i18n';
import { message } from '@utils/wuji-message';
import { map, compact } from 'lodash';

import AnncmntService from '@/services/announcement';

// 运行时管理列表包含全部环境、全部状态，不能覆盖导航栏使用的阅读列表。
const getManagementKeys = runtime => (runtime
  ? {
    list: 'managedAnncmntList',
    classifications: 'managedClassifications',
    listMutation: 'updateManagedAnncmntList',
    classificationsMutation: 'updateManagedClassifications',
  }
  : {
    list: 'anncmntList',
    classifications: 'classifications',
    listMutation: 'updateAnncmntList',
    classificationsMutation: 'updateClassifications',
  });

export default {
  state: {
    anncmntList: [],
    anncmntLoopList: [],
    classifications: new Set(),
    managedAnncmntList: [],
    managedClassifications: new Set(),
    runtimeSetting: null,
  },
  getters: {
    getListOfStyle: state => style => state.anncmntList.filter(anncmnt => anncmnt.style === style),
  },
  actions: {
    async fetchAnncmntList({ commit, rootState }, { projectId, filter = '', runtime = false, env } = {}) {
      try {
        const projectID = projectId || rootState.project.projectInfo?.id;
        const keys = getManagementKeys(runtime);
        const res = await AnncmntService.fetchAnncmntList(projectID, filter, { runtime, env });
        if (!filter) {
          // 仅在全量获取list时更新分类列表
          const classifications = new Set(compact(map(res, 'classification')));
          commit(keys.classificationsMutation, classifications);
        }
        commit(keys.listMutation, res);
        return res;
      } catch (err) {
        message.error($t('k_aafffa70', { arg_0: err.message }));
        return false;
      }
    },
    async deleteAnncmnt({ commit, state, rootState }, { projectId, id, runtime = false, env } = {}) {
      try {
        const projectID = projectId || rootState.project.projectInfo?.id;
        const keys = getManagementKeys(runtime);
        await AnncmntService.deleteAnncmnt({ projectId: projectID, id, runtime, env });
        const newList = state[keys.list].filter(item => item.id !== id);
        commit(keys.listMutation, newList);
        message.success($t('k_efd4b918'));
        return true;
      } catch (err) {
        message.error($t('k_7a29cb40', { arg_0: err.message }));
        return false;
      }
    },
    async updateAnncmnt({ commit, state, rootState }, { projectId, id, data, runtime = false, env } = {}) {
      try {
        const projectID = projectId || rootState.project.projectInfo?.id;
        const keys = getManagementKeys(runtime);
        const res = await AnncmntService.updateAnncmnt({ projectId: projectID, id, data, runtime, env });
        const newList = [...state[keys.list]];
        const index = newList.findIndex(item => item.id === id);
        if (index !== -1) newList[index] = res;
        commit(keys.listMutation, newList);
        if (data?.classification) {
          commit(keys.classificationsMutation, new Set([...state[keys.classifications], data.classification]));
        }
        message.success($t('k_38a75fb4'));
        return true;
      } catch (err) {
        message.error($t('k_56b62098', { arg_0: err.message }));
        return false;
      }
    },
    async createAnncmnt({ commit, state, rootState }, { projectId, formData, runtime = false, env } = {}) {
      try {
        const projectID = projectId || rootState.project.projectInfo?.id;
        const keys = getManagementKeys(runtime);
        const res = await AnncmntService.createAnncmnt({ projectId: projectID, data: formData, runtime, env });
        const newList = [res, ...state[keys.list]];
        commit(keys.listMutation, newList);
        if (formData?.classification) {
          commit(keys.classificationsMutation, new Set([...state[keys.classifications], formData.classification]));
        }
        message.success($t('k_9eddd076'));
        return true;
      } catch (err) {
        message.error($t('k_9680db30', { arg_0: err.message }));
        return false;
      }
    },
    async fetchAnncmntRuntimeList({ commit, rootState }, { projectId, filter = '' } = {}) {
      try {
        const projectID = projectId || rootState.project.projectInfo?.id;
        const res = await AnncmntService.fetchAnncmntRuntimeList(projectID, filter);
        if (!filter) {
          // 仅在全量获取list时更新分类列表
          const classifications = new Set(compact(map(res, 'classification')));
          commit('updateClassifications', classifications);
        }
        commit('updateAnncmntList', res);
      } catch (err) {
        console.error($t('k_aafffa70', { arg_0: err.message }));
      }
    },
    async fetchAnncmnLoopList({ commit, rootState }, { projectId } = {}) {
      try {
        const projectID = projectId || rootState.project.projectInfo?.id;
        const res = await AnncmntService.fetchAnncmnLoopList(projectID);
        commit('updateAnncmntLoopList', res);
      } catch (err) {
        console.error($t('k_3e3d2e52', { arg_0: err.message }));
      }
    },
    async readAnncmntRuntime({ commit, state, rootState }, { projectId, id, endTime } = {}) {
      try {
        const projectID = projectId || rootState.project.projectInfo?.id;
        await AnncmntService.readAnncmntRuntime(projectID, id, endTime);
        const newList = [...state.anncmntList];
        const index = newList.findIndex(item => item.id === id);
        newList[index].read = true;
        commit('updateAnncmntList', newList);
      } catch (err) {
        message.error($t('k_14ff5e3c', { arg_0: err.message }));
      }
    },
  },
  mutations: {
    updateManagedAnncmntList(state, payload) {
      state.managedAnncmntList = payload;
    },
    updateManagedClassifications(state, payload) {
      state.managedClassifications = new Set(payload);
    },
    updateRuntimeAnnouncementSetting(state, payload) {
      state.runtimeSetting = payload;
    },
    updateAnncmntList(state, payload) {
      state.anncmntList = payload;
    },
    updateAnncmntLoopList(state, payload) {
      state.anncmntLoopList = payload;
    },
    updateClassifications(state, payload) {
      if (
        state.classifications?.size === payload.size
        && [...state.classifications].every(item => payload.has(item))
      ) return; // not changed
      state.classifications = new Set(payload);
    },
  },
};
